import { ConflictType, type BidComparisonResponse } from '@consigli/types';
import { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiArrowUpRight, FiMaximize2, FiMinimize2 } from 'react-icons/fi';

import { Combobox } from '@/components';

type BidComparisonRow = BidComparisonResponse['rows'][number];
type BidComparisonCell = BidComparisonRow['cells'][number];
type BidComparisonCitation = NonNullable<BidComparisonCell['citation']>;

const CONFLICT_TONE: Record<string, { cell: string; dot: string; text: string }> = {
  [ConflictType.ADDRESSED]: {
    cell: 'bg-green-50',
    dot: 'bg-green-500',
    text: 'text-green-900',
  },
  [ConflictType.PARTIAL]: {
    cell: 'bg-amber-50',
    dot: 'bg-amber-500',
    text: 'text-amber-900',
  },
};

const ITEM_WIDTH = 240;
const CATEGORY_WIDTH = 150;
const WEIGHT_WIDTH = 110;
const CONTRACTOR_WIDTH = 300;

const ITEM_LEFT = 0;
const CATEGORY_LEFT = ITEM_WIDTH;
const WEIGHT_LEFT = ITEM_WIDTH + CATEGORY_WIDTH;

const CONTRACTOR_MIN_CELL_HEIGHT = '4.5rem';
const ACTIVE_ROW_BG = 'bg-blue-50/60';

const cellStyle = {
  minWidth: CONTRACTOR_WIDTH,
  width: CONTRACTOR_WIDTH,
  height: CONTRACTOR_MIN_CELL_HEIGHT,
} as const;

const stickyHeaderClass =
  'sticky top-0 z-30 bg-gray-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500';

interface ContractorCellProps {
  cell: BidComparisonCell | undefined;
  rowId: string;
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onViewSource?: (citation: BidComparisonCitation, cell: BidComparisonCell, rowId: string) => void;
}

const ContractorCell: FC<ContractorCellProps> = ({
  cell,
  rowId,
  isActive,
  isExpanded,
  onToggle,
  onViewSource,
}) => {
  const clipRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    if (isExpanded) return;
    const element = clipRef.current;
    if (!element) return;
    const measure = () => setIsOverflowing(element.scrollHeight - element.clientHeight > 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [cell?.value, isExpanded]);

  if (!cell) {
    return (
      <td
        className={`border-l border-gray-100 px-4 py-3 align-top text-gray-400 transition-colors ${!isActive ? 'group-hover:bg-gray-50/70' : ''}`}
        style={cellStyle}
      >
        —
      </td>
    );
  }

  const tone = cell.conflictType ? CONFLICT_TONE[cell.conflictType] : undefined;

  return (
    <td
      className={`relative border-l border-gray-100 align-top transition-colors ${tone ? tone.cell : !isActive ? 'group-hover:bg-gray-50/70' : ''}`}
      style={cellStyle}
    >
      <div
        className={`flex items-start justify-between gap-2 px-4 py-3 ${!isExpanded ? 'absolute inset-0' : ''}`}
      >
        <div
          ref={clipRef}
          className={`min-w-0 flex-1 ${!isExpanded ? 'h-full overflow-hidden' : ''}`}
        >
          <p
            className={`flex items-start gap-2 whitespace-pre-line text-sm leading-relaxed text-gray-800 ${tone ? `font-medium ${tone.text}` : ''}`}
          >
            {tone && (
              <span
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${tone.dot}`}
                aria-hidden="true"
              />
            )}
            {cell.value}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-center gap-1">
          {cell.citation && onViewSource && (
            <button
              type="button"
              onClick={() => onViewSource(cell.citation!, cell, rowId)}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              title="View source"
              aria-label="View source"
            >
              <FiArrowUpRight size={14} />
            </button>
          )}
          {isOverflowing && (
            <button
              type="button"
              onClick={onToggle}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              title={isExpanded ? 'Collapse' : 'Expand'}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <FiMinimize2 size={14} /> : <FiMaximize2 size={14} />}
            </button>
          )}
        </div>
      </div>
    </td>
  );
};

interface BidComparisonTableProps {
  data: BidComparisonResponse;
  weights: Record<string, number>;
  activeRowId?: string | null;
  onWeightChange: (rowId: string, value: string) => void;
  onViewSource?: (citation: BidComparisonCitation, cell: BidComparisonCell, rowId: string) => void;
  onViewReference?: (row: BidComparisonRow) => void;
}

export const BidComparisonTable: FC<BidComparisonTableProps> = ({
  data,
  weights,
  activeRowId,
  onWeightChange,
  onViewSource,
  onViewReference,
}) => {
  const { contractors, rows } = data;

  const [expandedRows, setExpandedRows] = useState<ReadonlySet<string>>(new Set());
  const [activeCategories, setActiveCategories] = useState<string[]>([]);

  const categoryOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((row) => row.category)))
        .sort()
        .map((category) => ({ label: category, value: category })),
    [rows],
  );

  const visibleRows = useMemo(
    () =>
      activeCategories.length === 0
        ? rows
        : rows.filter((row) => activeCategories.includes(row.category)),
    [rows, activeCategories],
  );

  const totalWeight = useMemo(
    () => Object.values(weights).reduce((total, weight) => total + (weight || 0), 0),
    [weights],
  );

  const toggleRow = useCallback((rowId: string) => {
    setExpandedRows((current) => {
      const next = new Set(current);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  const cellByContractor = useCallback(
    (cells: BidComparisonCell[], contractorId: string) =>
      cells.find((cell) => cell.contractorId === contractorId),
    [],
  );

  return (
    <div className="flex flex-col gap-3 isolate">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {categoryOptions.length > 1 && (
          <Combobox
            optionGroups={[{ label: 'Categories', options: categoryOptions }]}
            value={activeCategories}
            onValueChange={setActiveCategories}
            placeholder="Filter by category"
            fallbackLabel="No categories found"
            className="w-[240px]"
          />
        )}
        <div className="flex flex-wrap items-center gap-4 text-xs font-medium text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />
            Addressed
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
            Partially addressed
          </span>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white shadow-sm">
        <table className="border-collapse text-sm" style={{ minWidth: '100%' }}>
          <thead>
            <tr>
              <th
                className={`${stickyHeaderClass} z-40 border-b border-gray-200`}
                style={{ left: ITEM_LEFT, minWidth: ITEM_WIDTH, width: ITEM_WIDTH }}
              >
                Item
              </th>
              <th
                className={`${stickyHeaderClass} z-40 border-b border-gray-200`}
                style={{ left: CATEGORY_LEFT, minWidth: CATEGORY_WIDTH, width: CATEGORY_WIDTH }}
              >
                Category
              </th>
              <th
                className={`${stickyHeaderClass} z-40 border-b border-r border-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]`}
                style={{ left: WEIGHT_LEFT, minWidth: WEIGHT_WIDTH, width: WEIGHT_WIDTH }}
              >
                Weight
              </th>
              {contractors.map((contractor) => (
                <th
                  key={contractor.id}
                  className="sticky top-0 z-20 border-b border-l border-gray-200 bg-gray-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-900"
                  style={{ minWidth: CONTRACTOR_WIDTH, width: CONTRACTOR_WIDTH }}
                >
                  {contractor.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visibleRows.map((row) => {
              const isExpanded = expandedRows.has(row.id);
              const isActive = activeRowId === row.id;
              const stickyBg = isActive ? ACTIVE_ROW_BG : 'bg-white';
              return (
                <tr key={row.id} className={`group align-top ${isActive ? ACTIVE_ROW_BG : ''}`}>
                  <td
                    className={`sticky z-10 px-4 py-3 align-top font-medium leading-relaxed text-gray-900 transition-colors ${stickyBg} ${!isActive ? 'group-hover:bg-gray-50/70' : ''}`}
                    style={{ left: ITEM_LEFT, minWidth: ITEM_WIDTH, width: ITEM_WIDTH }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="min-w-0 flex-1">{row.item}</span>
                      {row.reference && onViewReference && (
                        <button
                          type="button"
                          onClick={() => onViewReference(row)}
                          className="mt-0.5 shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                          title="View tender document"
                          aria-label="View tender document"
                        >
                          <FiArrowUpRight size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td
                    className={`sticky z-10 px-4 py-3 align-top text-gray-600 transition-colors ${stickyBg} ${!isActive ? 'group-hover:bg-gray-50/70' : ''}`}
                    style={{ left: CATEGORY_LEFT, minWidth: CATEGORY_WIDTH, width: CATEGORY_WIDTH }}
                  >
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                      {row.category}
                    </span>
                  </td>
                  <td
                    className={`sticky z-10 border-r border-gray-200 px-4 py-3 align-top shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] transition-colors ${stickyBg} ${!isActive ? 'group-hover:bg-gray-50/70' : ''}`}
                    style={{ left: WEIGHT_LEFT, minWidth: WEIGHT_WIDTH, width: WEIGHT_WIDTH }}
                  >
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={weights[row.id] ?? 0}
                        onChange={(event) => onWeightChange(row.id, event.target.value)}
                        className="w-14 rounded border border-gray-300 px-2 py-1 text-right text-sm focus:border-gray-900 focus:outline-none"
                        aria-label={`Weight for ${row.item}`}
                      />
                      <span className="text-xs text-gray-400">%</span>
                    </div>
                  </td>
                  {contractors.map((contractor) => (
                    <ContractorCell
                      key={contractor.id}
                      cell={cellByContractor(row.cells, contractor.id)}
                      rowId={row.id}
                      isActive={isActive}
                      isExpanded={isExpanded}
                      onToggle={() => toggleRow(row.id)}
                      onViewSource={onViewSource}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50">
              <td
                className="sticky z-10 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500"
                style={{ left: ITEM_LEFT, minWidth: ITEM_WIDTH, width: ITEM_WIDTH }}
              >
                Total weighting
              </td>
              <td
                className="sticky z-10 bg-gray-50"
                style={{ left: CATEGORY_LEFT, minWidth: CATEGORY_WIDTH, width: CATEGORY_WIDTH }}
              />
              <td
                className={`sticky z-10 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] ${totalWeight === 100 ? 'text-green-700' : 'text-amber-700'}`}
                style={{ left: WEIGHT_LEFT, minWidth: WEIGHT_WIDTH, width: WEIGHT_WIDTH }}
              >
                {totalWeight}%
              </td>
              <td colSpan={contractors.length} className="border-l border-gray-200 bg-gray-50" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
